'use client'

import * as React from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Typography } from '@repo/design-system-cf/components/typography'
import { tw } from 'utils/classnames'
import { CartoonLoader } from './cartoon-loader'

const STAGE_EASE = [0.22, 1, 0.36, 1] as const

export type ResultPreviewPhase = 'loading' | 'generating' | 'ready'

export function resultPreviewPhase({
  isProcessing,
  isReady,
}: {
  isProcessing: boolean
  isReady: boolean
}): ResultPreviewPhase {
  if (isReady) {
    return 'ready'
  }
  if (isProcessing) {
    return 'generating'
  }
  return 'loading'
}

export function ResultPreview({
  phase,
  label,
  previewSrc,
  className,
  loaderClassName,
  children,
}: {
  phase: ResultPreviewPhase
  label: string
  previewSrc?: string | null
  className?: string
  loaderClassName?: string
  children: React.ReactNode
}) {
  if (phase === 'loading') {
    return <div aria-hidden className={tw(className, loaderClassName, 'bg-cf-grey-200')} />
  }

  return (
    <GeneratingStage
      isDone={phase === 'ready'}
      label={label}
      previewSrc={previewSrc}
      className={className}
      loaderClassName={loaderClassName}
    >
      {children}
    </GeneratingStage>
  )
}

export function ResultImage({ src }: { src: string }) {
  const [loadedSrc, setLoadedSrc] = React.useState<string | null>(null)
  const isLoaded = loadedSrc === src

  return (
    <div className={tw('size-full', isLoaded ? 'bg-cf-grey-0' : 'bg-cf-grey-200')}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt=""
        width={576}
        height={576}
        onLoad={() => setLoadedSrc(src)}
        className={tw('size-full object-contain', isLoaded ? 'opacity-100' : 'opacity-0')}
      />
    </div>
  )
}

export function GeneratingStage({
  isDone,
  label,
  previewSrc,
  className,
  loaderClassName,
  children,
}: {
  isDone: boolean
  label: string
  previewSrc?: string | null
  className?: string
  loaderClassName?: string
  children: React.ReactNode
}) {
  return (
    <AnimatePresence mode="wait">
      {isDone ? (
        <motion.div
          key="result"
          className={className}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: STAGE_EASE }}
        >
          {children}
        </motion.div>
      ) : (
        <motion.div
          key="generating"
          role="status"
          aria-label={label}
          className={tw('relative overflow-hidden', className, loaderClassName)}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.4, ease: STAGE_EASE }}
        >
          <CartoonLoader />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 p-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: 'spring', stiffness: 260, damping: 20 }}
          >
            {previewSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={previewSrc}
                alt=""
                width={80}
                height={80}
                className="size-20 rounded-2 bg-cf-grey-0 object-contain shadow-lg"
              />
            ) : null}
            <WigglyText text={label} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function WigglyText({ text }: { text: string }) {
  const reduceMotion = useReducedMotion()

  return (
    <Typography
      variant="cf-heading-03"
      className="text-center uppercase tracking-wide text-cf-grey-900"
    >
      {[...text].map((letter, index) => (
        <motion.span
          key={index}
          className="inline-block whitespace-pre"
          animate={reduceMotion ? undefined : { y: [0, -5, 0], rotate: [0, -6, 6, 0] }}
          transition={{
            duration: 0.8,
            ease: 'easeInOut',
            repeat: Infinity,
            repeatDelay: 0.6,
            delay: index * 0.05,
          }}
        >
          {letter}
        </motion.span>
      ))}
    </Typography>
  )
}
